import React, { useId } from 'react';
import './SideNavLayoutDocument.scss';

/**
 * SideNavLayoutDocument - Two-column page layout with fixed sidebar navigation
 *
 * Renders a document-level layout for pages that require persistent step-based
 * navigation alongside scrollable content. The sidebar (Sidecar) is fixed-width
 * (256px) on the left; the content area fills the remaining space.
 *
 * Responsive behavior (desktop-first):
 * - ≥1328px (desktop spec): 1216px content max-width with 32px left/right
 *   gutters (1280px total). Sidebar + content rendered side-by-side.
 * - 768–1327px: Same two-column layout; content fluidly shrinks within the
 *   1216px max-width clamp as the viewport narrows.
 * - ≤767px: Sidebar stacks above content; both go full-width.
 * - ≤479px: Tighter horizontal padding (mobile gutter token).
 *
 * All block content is rendered via a `data-block-placeholder` slot in the
 * content area, consumed by PolyRenderer's hydration system.
 */

/**
 * @polySemanticUsage About this layout: a two-column page with a fixed side navigation rail beside the main content column, where
 * the page's blocks render. Use it for documentation-style pages that need persistent section navigation; use
 * SingleColumnLayoutDocument for standard marketing pages.
 */
export interface SideNavLayoutDocumentProps {
  /** Category label displayed above the heading (e.g. "Get started") */
  category?: string;

  // Steps are declared as flat numbered props (step1Label…step8Href) rather than
  // a steps[] array because PolyC's flat field editor cannot author array props —
  // it only produces flat primitives. Do not refactor to an array.
  /** Sidebar heading (e.g. "5 steps to register") */
  heading?: string;

  /**
   * Accessible name for the sidebar when there is no heading (the heading
   * names it otherwise). Translatable so it is announced in the page language.
   * @polyLabel Sidebar Screen Reader Label
   * @polyHelp Name screen readers announce for the sidebar when it has no heading. Translated with the page.
   * @polyDefault Page navigation
   */
  ariaLabel?: string;

  /** Label for step 1 */
  step1Label?: string;

  /** Label for step 2 */
  step2Label?: string;

  /** Label for step 3 */
  step3Label?: string;

  /** Label for step 4 */
  step4Label?: string;

  /** Label for step 5 */
  step5Label?: string;

  /** Label for step 6 */
  step6Label?: string;

  /** Label for step 7 */
  step7Label?: string;

  /** Label for step 8 */
  step8Label?: string;

  /** Href for step 1 */
  step1Href?: string;

  /** Href for step 2 */
  step2Href?: string;

  /** Href for step 3 */
  step3Href?: string;

  /** Href for step 4 */
  step4Href?: string;

  /** Href for step 5 */
  step5Href?: string;

  /** Href for step 6 */
  step6Href?: string;

  /** Href for step 7 */
  step7Href?: string;

  /** Href for step 8 */
  step8Href?: string;

  /** Index of the currently active step (1-based). Highlights with orange left border. */
  activeStep?: number;

  /** Footer text displayed below the step list (e.g. "Configure your account–and start selling") */
  footerText?: string;

  /**
   * Optional children rendered inside the content area. When provided, these render
   * INSTEAD of the `data-block-placeholder` slot. Used for direct React composition
   * (Storybook stories, integration tests) where PolyR hydration isn't injecting blocks.
   * In production, leave undefined so PolyR can inject blocks via the placeholder.
   */
  children?: React.ReactNode;
}

/** Internal step data assembled from flat props */
interface StepData {
  label: string;
  href?: string;
  active: boolean;
}

export const SideNavLayoutDocument: React.FC<SideNavLayoutDocumentProps> = ({
  category,
  heading,
  ariaLabel,
  step1Label,
  step2Label,
  step3Label,
  step4Label,
  step5Label,
  step6Label,
  step7Label,
  step8Label,
  step1Href,
  step2Href,
  step3Href,
  step4Href,
  step5Href,
  step6Href,
  step7Href,
  step8Href,
  activeStep,
  footerText,
  children,
}) => {
  // Assemble steps from flat props
  const allSteps: (StepData | null)[] = [
    step1Label ? { label: step1Label, href: step1Href, active: activeStep === 1 } : null,
    step2Label ? { label: step2Label, href: step2Href, active: activeStep === 2 } : null,
    step3Label ? { label: step3Label, href: step3Href, active: activeStep === 3 } : null,
    step4Label ? { label: step4Label, href: step4Href, active: activeStep === 4 } : null,
    step5Label ? { label: step5Label, href: step5Href, active: activeStep === 5 } : null,
    step6Label ? { label: step6Label, href: step6Href, active: activeStep === 6 } : null,
    step7Label ? { label: step7Label, href: step7Href, active: activeStep === 7 } : null,
    step8Label ? { label: step8Label, href: step8Href, active: activeStep === 8 } : null,
  ];

  const steps = allSteps.filter((s): s is StepData => s !== null);

  // Stable ID for associating the <aside> landmark with its visible heading.
  // Using aria-labelledby instead of aria-label avoids duplicating the heading
  // text in two places and ensures screen readers announce the actual rendered
  // heading as the landmark name.
  const headingId = useId();

  return (
    <main className="poly-page poly-side-nav-layout">
      <div className="poly-side-nav-layout__container">
        {/* Sidebar / Sidecar */}
        <aside
          className="poly-side-nav-layout__sidebar"
          aria-labelledby={heading ? headingId : undefined}
          aria-label={heading ? undefined : ariaLabel || 'Page navigation'}
        >
          <nav className="poly-side-nav-layout__nav">
            {category && (
              <p className="poly-side-nav-layout__category">{category}</p>
            )}
            {heading && (
              <h2 id={headingId} className="poly-side-nav-layout__heading">{heading}</h2>
            )}
            {steps.length > 0 && (
              <ul className="poly-side-nav-layout__steps" role="list">
                {steps.map((step, index) => (
                  <li
                    key={`step-${index}`}
                    className={`poly-side-nav-layout__step${step.active ? ' poly-side-nav-layout__step--active' : ''}`}
                    aria-current={step.active ? 'step' : undefined}
                  >
                    {step.href ? (
                      <a href={step.href} className="poly-side-nav-layout__step-link">
                        {step.label}
                      </a>
                    ) : (
                      <span className="poly-side-nav-layout__step-text">
                        {step.label}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {footerText && (
              <p className="poly-side-nav-layout__footer-text">{footerText}</p>
            )}
          </nav>
        </aside>

        {/* Content area — children take precedence; otherwise PolyR injects blocks via the placeholder */}
        <div className="poly-side-nav-layout__content">
          {children ?? <div data-block-placeholder="block-placeholder-1"></div>}
        </div>
      </div>
    </main>
  );
};
